// Attachment reconciliation: make the local IDB blob store and the remote
// ./Vault/attachments/<id>.enc files match the id-set the vault manifest
// references. Attachments are immutable per id, so this is pure set-difference.
//
// SECURITY: this module moves CIPHERTEXT ONLY. It uses the vault's raw
// accessors (getRawAttachment / putRawAttachment) — never the decrypting
// accessor — so the master key and decrypted bytes never enter the sync path.
// A greppable invariant test forbids decrypt/secret tokens here. Keep it so.

import { encodeAttachmentBlob, decodeAttachmentBlob } from '../storage/attachment-blob-codec.js';

export async function reconcileAttachments({
  puter, vault, dir,
  upload = false, download = false, deleteRemote = false, deleteLocal = false,
}) {
  const referenced = vault.referencedAttachments();        // [{id, entryId}]
  const refIds = new Set(referenced.map((r) => r.id));
  const ownerOf = new Map(referenced.map((r) => [r.id, r.entryId]));
  const localIds = new Set(await vault.listAttachmentIds());
  const remoteIds = new Set(await listRemoteIds(puter, dir));

  const summary = { uploaded: [], downloaded: [], deletedRemote: [], deletedLocal: [] };
  const errors = [];
  // Attempt every file; collect failures and re-throw at the end so one bad
  // attachment never blocks the others (and a failure still prevents the
  // caller from advancing its synced marker — the next sync retries).
  const attempt = async (fn) => { try { await fn(); } catch (err) { errors.push(err); } };

  if (upload) {
    const pending = [...refIds].filter((id) => localIds.has(id) && !remoteIds.has(id));
    if (pending.length) await ensureDir(puter, dir);
    for (const id of pending) {
      await attempt(async () => {
        const raw = await vault.getRawAttachment(id);
        if (!raw) return;
        await puter.fs.write(`${dir}/${id}.enc`, encodeAttachmentBlob(raw));
        summary.uploaded.push(id);
      });
    }
  }

  if (download) {
    for (const id of refIds) {
      if (!remoteIds.has(id) || localIds.has(id)) continue;
      await attempt(async () => {
        const blob = await puter.fs.read(`${dir}/${id}.enc`);
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const { nonce, ciphertext } = decodeAttachmentBlob(bytes);
        await vault.putRawAttachment(id, ownerOf.get(id), { nonce, ciphertext });
        summary.downloaded.push(id);
      });
    }
  }

  if (deleteRemote) {
    for (const id of remoteIds) {
      if (refIds.has(id)) continue;
      await attempt(async () => {
        await puter.fs.delete(`${dir}/${id}.enc`);
        summary.deletedRemote.push(id);
      });
    }
  }

  if (deleteLocal) {
    for (const id of localIds) {
      if (refIds.has(id)) continue;
      await attempt(async () => {
        await vault.deleteRawAttachment(id);
        summary.deletedLocal.push(id);
      });
    }
  }

  if (errors.length) {
    const err = new Error(`reconcileAttachments: ${errors.length} attachment operation(s) failed`);
    err.errors = errors;
    throw err;
  }
  return summary;
}

async function listRemoteIds(puter, dir) {
  let items;
  try { items = await puter.fs.readdir(dir); }
  catch (err) { if (isNotFound(err)) return []; throw err; }
  return (items ?? [])
    .map((it) => (typeof it === 'string' ? it : it?.name))
    .filter((n) => typeof n === 'string' && n.endsWith('.enc'))
    .map((n) => n.slice(0, -'.enc'.length));
}

async function ensureDir(puter, dir) {
  if (!puter?.fs?.mkdir) return;
  try { await puter.fs.mkdir(dir, { createMissingParents: true }); }
  catch { try { await puter.fs.mkdir(dir); } catch { /* ignore — write will fail loudly */ } }
}

function isNotFound(err) {
  const hay = JSON.stringify(err, Object.getOwnPropertyNames(err ?? {})).toLowerCase();
  return ['subject_does_not_exist', 'enoent', 'not found', 'does not exist', 'no such file']
    .some((n) => hay.includes(n));
}
